/// <reference path="../../../../typings/index.d.ts" />
/// <reference path="./presets/presets-list.ts" />
/// <reference path="./presets/presets-validation-rules.ts" />
/// <reference path="./controls.module.ts" />

module Meevo.Components.Controls {
    'use strict';
    import IAttributes = angular.IAttributes;

    interface IParseLimitDate {
        timeRange: LimitDateTimeRangeType;
        amount: number;
        timeRangeLabel: string;
    }

    export const parseLimitDateRangeToValue = (value: string): IParseLimitDate => {
        let timeRange = <LimitDateTimeRangeType>value.match(/^\d+(\w+)/)[1].trim();
        let amount = parseInt(value.match(/^(\d+).+/)[1], 10);
        let timeRangeLabel = '';

        switch (timeRange) {
            case 'Days':
                if (amount > 1) {
                    timeRangeLabel = 'LABEL_DAYS';
                } else {
                    timeRangeLabel = 'LABEL_DAY';
                }
                break;
            case 'Week':
                timeRangeLabel = 'LABEL_WEEK';
                break;
            case 'Months':
                if (amount > 1) {
                    timeRangeLabel = 'LABEL_MONTHS';
                } else {
                    timeRangeLabel = 'LABEL_MONTH';
                }
                break;
            case 'Quarter':
                timeRangeLabel = 'LABEL_QUARTER';
                break;
            case 'Years':
                if (amount > 1) {
                    timeRangeLabel = 'LABEL_YEARS';
                } else {
                    timeRangeLabel = 'LABEL_YEAR';
                }
                break;
        }

        return {timeRange, amount,timeRangeLabel}
    };

    export enum DaysOfWeek {
        Sunday = 0,
        Monday = 1,
        Tuesday = 2,
        Wednesday = 3,
        Thursday = 4,
        Friday = 5,
        Saturday = 6
    }

    export enum ChangedDateEnum {
        From,
        To,
        Both // when using selectbox
    }

    interface IFromTo {
        from: Date,
        to: Date
    }

    interface IPreset {
        name: string,
        status: PresetStatus
    }

    export type RunReportFor = 'PastToday' | 'PastNotToday' | 'PastNotTodayAndYesterday' | 'FutureToday' | 'FutureNotToday' | 'All';

    interface IDateRangeScope extends ng.IScope {
        presetValidationRules: IValidationRuleObj[];
        presetFactory: Object;
        useCustomError: boolean;
        maxDate?: Date;
        minDate?: Date;
        from: Date;
        to: Date;
        presetList: Array<IPreset>;
        selectedPreset: IPreset;
        setCustomPreset(): void;
        predefinedPresetStatus?: PresetStatus;
        callback({changedDate: ChangedDateEnum, changedValue: IPreset});
        autoAdjustDate({changeDate: ChangedDateEnum});
        presetIsChangedManually: boolean;
        dateIsChangedAutomatically: boolean;
        initiateAsEmpty: boolean;
        dateRange?: number;
        startAfterEndDate?: boolean;
        validationMaxDate?: any;
        validationMinDate?: any;
        defaultValueTo?: Date;
        defaultValueFrom?: Date;
        customPreset?: Boolean;
        runReportFor?: RunReportFor;
        limitDateRangeTo?: string;
        dateRangeLimitByControl?: Boolean;
        ifForReport?: Boolean;
        startDayOfWeek?: number;
        isLocaleAware?: boolean;
        datePickerValidation: DatePickerValidationModel;
        limitDateRangeToVerbose?: IParseLimitDate;
        timeZoneOffset?: string;
    }

    export const DEFAULT_PRESET_LIST = [
        {name: 'LABEL_CUSTOM', status: PresetStatus.Custom},
        {name: 'LABEL_TODAY', status: PresetStatus.Today},
        {name: 'LABEL_YESTERDAY', status: PresetStatus.Yesterday},
        {name: 'LABEL_THIS_WEEK', status: PresetStatus.ThisWeek},
        {name: 'LABEL_LAST_WEEK', status: PresetStatus.LastWeek},
        {name: 'LABEL_PREVIOUS_TWO_WEEKS', status: PresetStatus.PreviousTwoWeeks},
        {name: 'LABEL_THIS_MONTH', status: PresetStatus.ThisMonth},
        {name: 'LABEL_LAST_MONTH', status: PresetStatus.LastMonth},
        {name: 'LABEL_THIS_QUARTER', status: PresetStatus.ThisQuarter},
        {name: 'LABEL_LAST_QUARTER', status: PresetStatus.LastQuarter},
        {name: 'LABEL_NEXT_QUARTER', status: PresetStatus.NextQuarter},
        {name: 'LABEL_THIS_YEAR', status: PresetStatus.ThisYear},
        {name: 'LABEL_LAST_YEAR', status: PresetStatus.LastYear},
        {name: 'LABEL_TOMORROW', status: PresetStatus.Tomorrow},
        {name: 'LABEL_NEXT_WEEK', status: PresetStatus.NextWeek},
        {name: 'LABEL_NEXT_MONTH', status: PresetStatus.NextMonth},
        {name: 'LABEL_NEXT_YEAR', status: PresetStatus.NextYear}
    ];

    class DatePickerValidationModel {

        private _minDate: Date;
        private _maxDate: Date;

        private readonly limitDateRangeTo: boolean;
        public readonly limitDateRangeToDays: number;

        get minDate(): Date {
            return this._minDate;
        }

        set minDate(date: Date) {
            if (date instanceof Date) {
                this._minDate = angular.copy(date);
                this.updateFromToDates();
            }
        }

        get maxDate(): Date {
            return this._maxDate;
        }

        set maxDate(date: Date) {
            if (date instanceof Date) {
                this._maxDate = angular.copy(date);
                this.updateFromToDates();
            }
        }

        constructor(private scope: IDateRangeScope, private presetsFactory, private formController: ng.IFormController) {
            this.limitDateRangeTo = typeof this.scope.limitDateRangeTo === 'string' && this.scope.limitDateRangeTo.length > 0;
            if (this.limitDateRangeTo) {
                let {timeRange, amount} = parseLimitDateRangeToValue(this.scope.limitDateRangeTo);

                switch (timeRange) {
                    case 'Days':
                        this.limitDateRangeToDays = amount;
                        break;
                    case 'Week':
                        this.limitDateRangeToDays = amount * 7;
                        break;
                    case 'Months':
                        this.limitDateRangeToDays = amount * 31;
                        break;
                    case 'Quarter':
                        this.limitDateRangeToDays = amount * 93;
                        break;
                    case 'Years':
                        this.limitDateRangeToDays = amount * 365;
                        break;
                }
            }

            this.prepareMaxMinDates();
            this.initWatchers();
        }

        private prepareMaxMinDates() {
            this._minDate = this.scope.minDate;
            this._maxDate = this.scope.maxDate;

            if (this.scope.validationMaxDate) {
                if (!isNaN(this.scope.validationMaxDate)) {
                    this.maxDate = moment().add(Number(this.scope.validationMaxDate), 'd').toDate();
                } else {
                    this.presetsFactory[PresetStatus[this.scope.validationMaxDate]].getDate(this.scope);
                    this.maxDate = this.scope.to;
                }
            }

            if (this.scope.validationMinDate) {
                if (!isNaN(this.scope.validationMinDate)) {
                    this.maxDate = moment().subtract(Number(this.scope.validationMinDate), 'd').toDate();
                } else {
                    this.presetsFactory[PresetStatus[this.scope.validationMinDate]].getDate(this.scope);
                    this.minDate = this.scope.from;
                }
            }

            if (typeof this.scope.runReportFor === 'string' && this.scope.runReportFor.length > 0) {
                this.checkValueRunReportFor();
            }
        }

        private checkValueRunReportFor() {
            function businessMoment(scope) {
                if (scope['timeZoneOffset'] && scope['timeZoneOffset'] !== '') {
                    let localUtcOffset = moment().utcOffset();
                    let businessUtcOffset = moment().utcOffset(scope.timeZoneOffset).utcOffset();
                    let businessOffsetFromLocal = businessUtcOffset - localUtcOffset;
                    return moment().add('minutes', businessOffsetFromLocal);
                }
                return moment();
            }

            switch (this.scope.runReportFor) {
                case 'PastToday':
                    this.maxDate = businessMoment(this.scope).endOf('day').toDate();
                    break;
                case 'PastNotToday':
                    this.maxDate = businessMoment(this.scope).add(-1, 'days').endOf('day').toDate();
                    break;
                case 'PastNotTodayAndYesterday':
                    this.maxDate = businessMoment(this.scope).add(-2, 'days').endOf('day').toDate();
                    break;
                case 'FutureToday':
                    this.minDate = businessMoment(this.scope).startOf('day').toDate();
                    break;
                case 'FutureNotToday':
                    this.minDate = businessMoment(this.scope).add(1, 'days').startOf('day').toDate();
                    break;
            }
        }

        private initWatchers() {
            if(this.limitDateRangeTo) {
                this.scope.$watch(() => [this.scope.to, this.scope.from], () => {
                    this.formController['dateRangeWithPresets'].$setValidity(
                        'limitDateRangeToDays',
                        LimitDateRangeToRules.Days(this.scope.from, this.scope.to, this.limitDateRangeToDays),
                        this.formController['from']
                    );
                }, true);
            }

            this.scope.$watch('runReportFor', (newValue, oldValue) => {
                if(newValue !== oldValue) {
                    this.prepareMaxMinDates();
                    this.updateFromToDates();
                }
            });

            if (this.scope.timeZoneOffset === '') {
                this.scope.$watch('timeZoneOffset', (newValue, oldValue) => {
                    if(newValue !== '' && newValue !== oldValue) {
                        this.prepareMaxMinDates();
                        this.updateFromToDates();
                    }
                });
            }
        }

        private updateFromToDates(): void {
            if (angular.isDate(this.scope.to) && !this.scope.initiateAsEmpty && this.maxDate < this.scope.to) {
                this.scope.to = moment(this.maxDate).endOf('day').toDate();
            }
            if (angular.isDate(this.scope.from) && !this.scope.initiateAsEmpty && this.maxDate < this.scope.from) {
                this.scope.from = moment(this.maxDate).startOf('day').toDate();
            }

            if (angular.isDate(this.scope.from) && !this.scope.initiateAsEmpty && this.minDate > this.scope.from) {
                this.scope.from = moment(this.minDate).startOf('day').toDate();
            }
            if (angular.isDate(this.scope.to) && !this.scope.initiateAsEmpty && this.minDate > this.scope.to) {
                this.scope.to = moment(this.minDate).endOf('day').toDate();
            }
        }

    }

    export class MeevoDateRangeWithPresets implements angular.IDirective {
        restrict = 'E';
        require = '^form';
        scope = {
            minDate: '=?',
            maxDate: '=?',
            from: '=',
            to: '=',
            disabled: '=ngDisabled',
            required: '=ngRequired',
            tabIndex: '@tabIndex',
            contentKeyFrom: '@',
            contentKeyTo: '@',
            contentKeySelect: '@',
            useCustomError: '@',
            presetList: '=?',
            predefinedPresetStatus: '=?',
            callback: '&?',
            autoAdjustDate: '&?',
            initiateAsEmpty: '=?',
            dateRange: '=?',
            validationMaxDate: '=?',
            validationMinDate: '=?',
            defaultValueTo: '=?',
            defaultValueFrom: '=?',
            hidePresets: '=?',
            customPreset: '=?',
            runReportFor: '@?',
            limitDateRangeTo: '@?', // Examples: 1 Days, 2 Week, 2 Months, ...
            dateRangeLimitByControl: '=?',
            ifForReport: '=?',
            startDayOfWeek: '=?',
            isLocaleAware: '=?',
            fullLine: '=?',
            oneLine: '=?',
            selectedPreset: '=?',
            forceCallback: '=?',
            skipFilterPreset: '=?',
            timeZoneOffset: '=?',
        };
        templateUrl = 'app/components/controls/dateRangeWithPresetsTemplate.html';
        link = (scope: IDateRangeScope, element: ng.IAugmentedJQuery, attrs: IAttributes, formController: ng.IFormController) => {
            scope.startAfterEndDate = false;
            scope.useCustomError = attrs['useCustomError'];
            scope.presetList = scope.presetList || DEFAULT_PRESET_LIST;
            scope.presetValidationRules = [];

            const options = this.returnParentOptions(scope);
            scope.hideDatePickersAndCustomOption = (!!options && options.hideDatePickers == "true");
            scope.hideLongDateRanges = (!!options && options.hideLongDateRanges == "true");

            if (scope.hideDatePickersAndCustomOption && scope.presetList[0].name == "LABEL_CUSTOM") {
                scope.presetList = _.clone(scope.presetList).splice(1, scope.presetList.length-1);
            }

            if (scope.hideLongDateRanges) {
                if (options.displayName === 'DateRangeYear') {
                    scope.presetList = scope.presetList.filter(preset => {
                        return preset.status === PresetStatus.LastThirteenMonths ||
                               preset.status === PresetStatus.ThisYear
                    });
                } else {
                    for (var i = 0; i < scope.presetList.length; i++) {
                        if (scope.presetList[i].name.indexOf("YEAR") != -1 ||
                            scope.presetList[i].name.indexOf("QUARTER") != -1) {
                            //scope.presetList = _.clone(scope.presetList).splice(1, scope.presetList.length - 1);
                            scope.presetList.splice(i, 1);
                            i--;
                        }
                    }
                }
            }

            this.buildPresetFactory(scope);

            this.buildValidationRules(scope);

            if (scope.defaultValueTo) {
                scope.to = moment(scope.defaultValueTo).toDate()
            }

            if (scope.defaultValueFrom) {
                scope.from = moment(scope.defaultValueFrom).toDate()
            }

            scope.presetIsChangedManually = false;

            if (!angular.isDefined(scope.selectedPreset)) {
                if (angular.isDefined(scope.predefinedPresetStatus)) {
                    scope.selectedPreset = _.findWhere(scope.presetList, { status: scope.predefinedPresetStatus });
                } else {
                    if (scope.runReportFor === 'PastNotTodayAndYesterday') {
                        scope.presetList = scope.presetList.filter((preset) => preset.status !== PresetStatus.Today && preset.status !== PresetStatus.Yesterday);
                    }
                    scope.selectedPreset = _.findWhere(scope.presetList, { status: PresetStatus.Custom }) || scope.presetList[0];
                }
            }

            // predefine model if empty
            if (angular.isUndefined(scope.from) || (angular.isDate(scope.from) === false)) {
                scope.from = scope.initiateAsEmpty ? null : moment().startOf('day').toDate();

                if (scope.from && scope.minDate && moment(scope.from).isBefore(scope.minDate)) {
                    scope.from = moment(scope.minDate).add(1, 'days').startOf('day').toDate()
                }
            }
            if (angular.isUndefined(scope.to) || (angular.isDate(scope.to) === false)) {
                scope.to = scope.initiateAsEmpty ? null : moment(scope.from).endOf('day').toDate();

                if (scope.to && scope.maxDate && moment(scope.to).isAfter(scope.maxDate)) {
                    scope.to = moment(scope.maxDate).add(-1, 'days').endOf('day').toDate()
                }
            }

            scope.datePickerValidation = new DatePickerValidationModel(scope, this.presetFactory, formController);

            element.find('.md-datepicker-input').on('change', scope.setCustomPreset);
            scope.setCustomPreset = () => {
                scope.selectedPreset = _.findWhere(scope.presetList, { status: PresetStatus.Custom }) || scope.presetList[0];
                scope.updateToDate();
            };

            scope.updateToDate = () => {
                if (scope.to) scope.to = moment(scope.to).endOf('day').toDate();
            }

            if (!angular.isUndefined(scope.customPreset)) {
                scope.$watch(() => {
                        return scope.customPreset;
                    },
                    (newVal: Boolean, oldVal: Boolean): void => {
                        scope.selectedPreset = _.findWhere(
                            scope.presetList,
                            { status: scope.selectedPreset ? scope.selectedPreset.status : PresetStatus.Custom }
                        ) || scope.presetList[0];
                        scope.updateToDate();
                    });
            }

            scope.$watch(() => {
                return scope.from;
            }, (newVal: Date, oldVal: Date): void => {
                if (newVal && newVal !== oldVal) {
                    let mNewVal = moment(newVal),
                        mTo = moment(scope.to),
                        range: number;

                    // Reset date range dropdown if matches preset of Today
                    if (scope.predefinedPresetStatus && scope.predefinedPresetStatus === PresetStatus.Today) {
                        if (mNewVal.isSame(mTo, 'day') && mNewVal.isSame(moment(), 'day')) {
                            scope.selectedPreset = _.findWhere(scope.presetList, {status: scope.predefinedPresetStatus});
                        }
                    }

                    if (scope.dateRange) {
                        range = mTo.diff(mNewVal, 'days', true) - scope.dateRange;
                        scope.startAfterEndDate = !mNewVal.isBefore(mTo);

                        formController['dateRangeWithPresets']
                            .$setValidity('daterange',
                                range < 1 && range >= -scope.dateRange,
                                formController['from']);
                    } else {
                        formController['dateRangeWithPresets']
                            .$setValidity('daterange',
                                mNewVal.isBefore(mTo) || mNewVal.isSame(mTo),
                                formController['from']);
                    }


                    if (scope.callback && angular.isFunction(scope.callback) && (!scope.presetIsChangedManually || scope.forceCallback)) {
                        scope.callback({changedDate: ChangedDateEnum.From, changedValue: scope.selectedPreset});
                    }
                    if (scope.autoAdjustDate && angular.isFunction(scope.autoAdjustDate) && !scope.presetIsChangedManually && !scope.dateIsChangedAutomatically) {
                        scope.dateIsChangedAutomatically = true;
                        scope.autoAdjustDate({changeDate: ChangedDateEnum.From});
                        this.$timeout(() => {
                            scope.dateIsChangedAutomatically = false;
                        }, 0)            
                    }

                    scope.enforceValidationJsonDateRangeLimitRules();
                }
            }, true);

            scope.$watch(() => {
                return scope.to;
            }, (newVal: Date, oldVal: Date): void => {
                if (newVal && newVal !== oldVal) {
                    let mNewVal = moment(newVal),
                        mFrom = moment(scope.from),
                        range: number;

                    // Reset date range dropdown if matches preset of Today
                    if (scope.predefinedPresetStatus && scope.predefinedPresetStatus === PresetStatus.Today) {
                        if (mNewVal.isSame(mFrom, 'day') && mNewVal.isSame(moment(), 'day')) {
                            scope.selectedPreset = _.findWhere(scope.presetList, {status: scope.predefinedPresetStatus});
                        }
                    }

                    if (scope.dateRange) {
                        range = mFrom.diff(mNewVal, 'days', true) + scope.dateRange;
                        scope.startAfterEndDate = !mNewVal.isAfter(mFrom);

                        formController['dateRangeWithPresets']
                            .$setValidity('daterange',
                                range > -1 && range <= scope.dateRange,
                                formController['from']);
                    } else {
                        formController['dateRangeWithPresets']
                            .$setValidity('daterange',
                                mNewVal.isAfter(mFrom) || mNewVal.isSame(mFrom),
                                formController['to']);
                    }
                    if (scope.callback && angular.isFunction(scope.callback) && !scope.presetIsChangedManually) {
                        scope.callback({changedDate: ChangedDateEnum.To, changedValue: scope.selectedPreset});
                    }
                    if (scope.autoAdjustDate && angular.isFunction(scope.autoAdjustDate) && !scope.presetIsChangedManually && !scope.dateIsChangedAutomatically) {
                        scope.dateIsChangedAutomatically = true;
                        scope.autoAdjustDate({changeDate: ChangedDateEnum.To});
                        this.$timeout(() => {
                            scope.dateIsChangedAutomatically = false;
                        }, 0)
                    }

                    scope.enforceValidationJsonDateRangeLimitRules();
                }
            }, true);

            scope.$watch(() => {
                return scope.selectedPreset;
            }, (newVal: IPreset): void => {
                if (newVal) {
                    scope.presetIsChangedManually = true;
                    let options = <IPresetOptions>{
                        ifForReport: scope.ifForReport,
                        isLocaleAware: scope.isLocaleAware,
                        minDate: scope.datePickerValidation.minDate,
                        maxDate: scope.datePickerValidation.maxDate
                    };

                    this.presetFactory[newVal.status].getDate(scope, options);

                    // ensure from & to are within minDate & maxDate
                    if (scope.from && scope.datePickerValidation.minDate && moment(scope.from).isBefore(scope.datePickerValidation.minDate)) {
                        scope.from = moment(scope.datePickerValidation.minDate).startOf('day').toDate();
                    }

                    if (scope.from && scope.datePickerValidation.maxDate && moment(scope.from).isAfter(scope.datePickerValidation.maxDate)) {
                        scope.from = moment(scope.datePickerValidation.maxDate).startOf('day').toDate();
                    }

                    if (scope.to && scope.datePickerValidation.minDate && moment(scope.to).isBefore(scope.datePickerValidation.minDate)) {
                        scope.to = moment(scope.datePickerValidation.minDate).endOf('day').toDate();
                    }

                    if (scope.to && scope.datePickerValidation.maxDate && moment(scope.to).isAfter(scope.datePickerValidation.maxDate)) {
                        scope.to = moment(scope.datePickerValidation.maxDate).endOf('day').toDate();
                    }

                    if (scope.callback && angular.isFunction(scope.callback)) {
                        this.$timeout(() => {
                            if (newVal.status !== PresetStatus.Custom) {
                                scope.callback({changedDate: ChangedDateEnum.Both, changedValue: scope.selectedPreset});
                            }

                            scope.presetIsChangedManually = false;
                        }, 0);
                    }
                }
            });

            if (scope.dateRangeLimitByControl) {
                // watch for updates to date range limit when using DateRangeLimitByControl
                scope.$watch(() => scope.limitDateRangeTo, (newVal: string): void => {
                    if (newVal) {
                        scope.presetValidationRules = [];
                        this.buildValidationRules(scope)
                        scope.datePickerValidation = new DatePickerValidationModel(scope, this.presetFactory, formController);
                        scope.presetList = DEFAULT_PRESET_LIST.filter(preset => {
                            return this.presetFactory[preset.status].validate(scope.presetValidationRules);
                        });
                    }
                });
            }

            if (scope.timeZoneOffset === '') {
                scope.$watch('timeZoneOffset', (newValue, oldValue) => {
                    if(newValue !== '' && newValue !== oldValue) {
                        this.buildPresetFactory(scope);
                    }
                });
            }

            if (!scope.skipFilterPreset) {
                this.filterPreList(scope);
            }

            scope.enforceValidationJsonDateRangeLimitRules = () =>
            {
                if (scope.datePickerValidation.limitDateRangeToDays != null) {
                    formController['dateRangeWithPresets'].$setValidity(
                         'limitDateRangeToDays',
                         LimitDateRangeToRules.Days(scope.from, scope.to, scope.datePickerValidation.limitDateRangeToDays),
                         formController['from']);
                }
            }
        };

        buildPresetFactory(scope: IDateRangeScope) {
            this.presetFactory = {
                [CustomPreset.presetStatus]: new CustomPreset({
                    ifForReport: scope.ifForReport,
                    isLocaleAware: scope.isLocaleAware,
                    startDayOfWeek: scope.startDayOfWeek,
                    timeZoneOffset: scope.timeZoneOffset
                }),
                [TodayPreset.presetStatus]: new TodayPreset({
                    ifForReport: scope.ifForReport,
                    isLocaleAware: scope.isLocaleAware,
                    startDayOfWeek: scope.startDayOfWeek,
                    timeZoneOffset: scope.timeZoneOffset
                }),
                [YesterdayPreset.presetStatus]: new YesterdayPreset({
                    ifForReport: scope.ifForReport,
                    isLocaleAware: scope.isLocaleAware,
                    startDayOfWeek: scope.startDayOfWeek,
                    timeZoneOffset: scope.timeZoneOffset
                }),
                [ThisWeekPreset.presetStatus]: new ThisWeekPreset({
                    ifForReport: scope.ifForReport,
                    isLocaleAware: scope.isLocaleAware,
                    startDayOfWeek: scope.startDayOfWeek,
                    timeZoneOffset: scope.timeZoneOffset
                }),
                [LastWeekPreset.presetStatus]: new LastWeekPreset({
                    ifForReport: scope.ifForReport,
                    isLocaleAware: scope.isLocaleAware,
                    startDayOfWeek: scope.startDayOfWeek,
                    timeZoneOffset: scope.timeZoneOffset
                }),
                [LastSevenDaysPreset.presetStatus]: new LastSevenDaysPreset({
                    ifForReport: scope.ifForReport,
                    isLocaleAware: scope.isLocaleAware,
                    startDayOfWeek: scope.startDayOfWeek,
                    timeZoneOffset: scope.timeZoneOffset
                }),
                [PreviousTwoWeeksPreset.presetStatus]: new PreviousTwoWeeksPreset({
                    ifForReport: scope.ifForReport,
                    isLocaleAware: scope.isLocaleAware,
                    startDayOfWeek: scope.startDayOfWeek,
                    timeZoneOffset: scope.timeZoneOffset
                }),
                [ThisMonthPreset.presetStatus]: new ThisMonthPreset({
                    ifForReport: scope.ifForReport,
                    isLocaleAware: scope.isLocaleAware,
                    startDayOfWeek: scope.startDayOfWeek,
                    timeZoneOffset: scope.timeZoneOffset
                }),
                [LastMonthPreset.presetStatus]: new LastMonthPreset({
                    ifForReport: scope.ifForReport,
                    isLocaleAware: scope.isLocaleAware,
                    startDayOfWeek: scope.startDayOfWeek,
                    timeZoneOffset: scope.timeZoneOffset
                }),
                [ThisQuarterPreset.presetStatus]: new ThisQuarterPreset({
                    ifForReport: scope.ifForReport,
                    isLocaleAware: scope.isLocaleAware,
                    startDayOfWeek: scope.startDayOfWeek,
                    timeZoneOffset: scope.timeZoneOffset
                }),
                [LastQuarterPreset.presetStatus]: new LastQuarterPreset({
                    ifForReport: scope.ifForReport,
                    isLocaleAware: scope.isLocaleAware,
                    startDayOfWeek: scope.startDayOfWeek,
                    timeZoneOffset: scope.timeZoneOffset
                }),
                [NextQuarterPreset.presetStatus]: new NextQuarterPreset({
                    ifForReport: scope.ifForReport,
                    isLocaleAware: scope.isLocaleAware,
                    startDayOfWeek: scope.startDayOfWeek,
                    timeZoneOffset: scope.timeZoneOffset
                }),
                [ThisYearPreset.presetStatus]: new ThisYearPreset({
                    ifForReport: scope.ifForReport,
                    isLocaleAware: scope.isLocaleAware,
                    startDayOfWeek: scope.startDayOfWeek,
                    timeZoneOffset: scope.timeZoneOffset
                }),
                [LastYearPreset.presetStatus]: new LastYearPreset({
                    ifForReport: scope.ifForReport,
                    isLocaleAware: scope.isLocaleAware,
                    startDayOfWeek: scope.startDayOfWeek,
                    timeZoneOffset: scope.timeZoneOffset
                }),
                [TomorrowPreset.presetStatus]: new TomorrowPreset({
                    ifForReport: scope.ifForReport,
                    isLocaleAware: scope.isLocaleAware,
                    startDayOfWeek: scope.startDayOfWeek,
                    timeZoneOffset: scope.timeZoneOffset
                }),
                [NextWeekPreset.presetStatus]: new NextWeekPreset({
                    ifForReport: scope.ifForReport,
                    isLocaleAware: scope.isLocaleAware,
                    startDayOfWeek: scope.startDayOfWeek,
                    timeZoneOffset: scope.timeZoneOffset
                }),
                [NextMonthPreset.presetStatus]: new NextMonthPreset({
                    ifForReport: scope.ifForReport,
                    isLocaleAware: scope.isLocaleAware,
                    startDayOfWeek: scope.startDayOfWeek,
                    timeZoneOffset: scope.timeZoneOffset
                }),
                [NextYearPreset.presetStatus]: new NextYearPreset({
                    ifForReport: scope.ifForReport,
                    isLocaleAware: scope.isLocaleAware,
                    startDayOfWeek: scope.startDayOfWeek,
                    timeZoneOffset: scope.timeZoneOffset
                }),
                [LastTwelveMonthsPreset.presetStatus]: new LastTwelveMonthsPreset({
                    ifForReport: scope.ifForReport,
                    isLocaleAware: scope.isLocaleAware,
                    startDayOfWeek: scope.startDayOfWeek,
                    timeZoneOffset: scope.timeZoneOffset
                }),
                [LastTwentyFourHoursPreset.presetStatus]: new LastTwentyFourHoursPreset({
                    ifForReport: scope.ifForReport,
                    isLocaleAware: scope.isLocaleAware,
                    startDayOfWeek: scope.startDayOfWeek,
                    timeZoneOffset: scope.timeZoneOffset
                }),
                [LastFourteenDaysPreset.presetStatus]: new LastFourteenDaysPreset({
                    ifForReport: scope.ifForReport,
                    isLocaleAware: scope.isLocaleAware,
                    startDayOfWeek: scope.startDayOfWeek,
                    timeZoneOffset: scope.timeZoneOffset
                }),
                [LastThirtyDaysPreset.presetStatus]: new LastThirtyDaysPreset({
                    ifForReport: scope.ifForReport,
                    isLocaleAware: scope.isLocaleAware,
                    startDayOfWeek: scope.startDayOfWeek,
                    timeZoneOffset: scope.timeZoneOffset
                }),
                [LastSixtyDaysPreset.presetStatus]: new LastSixtyDaysPreset({
                    ifForReport: scope.ifForReport,
                    isLocaleAware: scope.isLocaleAware,
                    startDayOfWeek: scope.startDayOfWeek,
                    timeZoneOffset: scope.timeZoneOffset
                }),
                [LastNinetyDaysPreset.presetStatus]: new LastNinetyDaysPreset({
                    ifForReport: scope.ifForReport,
                    isLocaleAware: scope.isLocaleAware,
                    startDayOfWeek: scope.startDayOfWeek,
                    timeZoneOffset: scope.timeZoneOffset
                }),
                [LastOneTwentyDaysPreset.presetStatus]: new LastOneTwentyDaysPreset({
                    ifForReport: scope.ifForReport,
                    isLocaleAware: scope.isLocaleAware,
                    startDayOfWeek: scope.startDayOfWeek,
                    timeZoneOffset: scope.timeZoneOffset
                }),
                [LastOneEightyDaysPreset.presetStatus]: new LastOneEightyDaysPreset({
                    ifForReport: scope.ifForReport,
                    isLocaleAware: scope.isLocaleAware,
                    startDayOfWeek: scope.startDayOfWeek,
                    timeZoneOffset: scope.timeZoneOffset
                }),
                [LastThirteenMonthsPreset.presetStatus]: new LastThirteenMonthsPreset({
                    ifForReport: scope.ifForReport,
                    isLocaleAware: scope.isLocaleAware,
                    startDayOfWeek: scope.startDayOfWeek,
                    timeZoneOffset: scope.timeZoneOffset
                }),
                [SpecificYearPreset.presetStatus]: new SpecificYearPreset({
                    ifForReport: scope.ifForReport,
                    isLocaleAware: scope.isLocaleAware,
                    startDayOfWeek: scope.startDayOfWeek,
                    timeZoneOffset: scope.timeZoneOffset
                }),
                [LastTwelveMonthsIncludingTodayPreset.presetStatus]: new LastTwelveMonthsIncludingTodayPreset({
                    ifForReport: scope.ifForReport,
                    isLocaleAware: scope.isLocaleAware,
                    startDayOfWeek: scope.startDayOfWeek
                }),
                [YearToDatePreset.presetStatus]: new YearToDatePreset({
                    ifForReport: scope.ifForReport,
                    isLocaleAware: scope.isLocaleAware,
                    startDayOfWeek: scope.startDayOfWeek
                }),
            }
        }

        buildValidationRules(scope: IDateRangeScope) {
            let dateNow = moment().startOf('day');

            if (scope.validationMaxDate) {
                if (!isNaN(scope.validationMaxDate)) {
                    scope.maxDate = moment().add(Number(scope.validationMaxDate), 'd').toDate();
                } else {
                    this.presetFactory[PresetStatus[scope.validationMaxDate]].getDate(scope);
                    scope.maxDate = scope.to;

                    scope.from = scope.initiateAsEmpty ? null : dateNow.isBefore(scope.to, 'day') ? dateNow.toDate() : moment(scope.to).toDate();
                    scope.to = scope.initiateAsEmpty ? null : dateNow.isBefore(scope.to, 'day') ? dateNow.toDate() : moment(scope.to).toDate();
                }

                scope.presetValidationRules.push({
                    rule: scope.ifForReport ? RULES.maxDateReport : RULES.maxDate,
                    data: [scope.maxDate]
                });
            }

            if (scope.validationMinDate) {
                if (!isNaN(scope.validationMinDate)) {
                    scope.minDate = moment().subtract(Number(scope.validationMinDate), 'd').toDate();
                } else {
                    this.presetFactory[PresetStatus[scope.validationMinDate]].getDate(scope);
                    scope.minDate = scope.from;

                    scope.from = scope.initiateAsEmpty ? null : dateNow.isAfter(scope.from, 'day') ? dateNow.toDate() : moment(scope.from).toDate();
                    scope.to = scope.initiateAsEmpty ? null : dateNow.isAfter(scope.from, 'day') ? dateNow.toDate() : moment(scope.from).toDate();
                }

                scope.presetValidationRules.push({
                    rule: scope.ifForReport ? RULES.minDateReport : RULES.minDate,
                    data: [scope.minDate]
                });
            }

            if (typeof RunReportForRules[scope.runReportFor] === 'function') {
                scope.presetValidationRules.push({rule: RunReportForRules[scope.runReportFor]});
            }

            if (scope.dateRange) {
                scope.presetValidationRules.push({
                    rule: LimitDateRangeToRules['Days'],
                    data: [scope.dateRange]
                });
            }

            if (typeof scope.limitDateRangeTo === 'string' && scope.limitDateRangeTo.length > 0) {
                let {timeRange, amount, timeRangeLabel} = parseLimitDateRangeToValue(scope.limitDateRangeTo);
                scope.limitDateRangeToVerbose = {timeRange, amount, timeRangeLabel};
                scope.isLimitInDaysVisible = scope.ifForReport && ['Months', 'Quarter', 'Years'].indexOf(timeRange) > -1;

                if (typeof LimitDateRangeToRules[timeRange] === 'function') {
                    scope.presetValidationRules.push({
                        rule: LimitDateRangeToRules[timeRange],
                        data: [amount]
                    });
                } else {
                    console.error(`Unknown rule "${timeRange}"`);
                }
            }
        }

        returnParentOptions(root: any) {
            if (!root) {
                return null;
            }

            if (root.options != null) {
                return root.options;
            }

            return this.returnParentOptions(root.$parent);
        }

        presetFactory = {};

        constructor(protected $timeout) {
        }

        static factory(): angular.IDirectiveFactory {
            let directive = ($timeout) => {
                return new MeevoDateRangeWithPresets($timeout);
            };
            directive.$inject = ['$timeout'];
            return directive;
        }

        private filterPreList(scope: IDateRangeScope): void {
            scope.presetList = scope.presetList.filter(preset => {
                return this.presetFactory[preset.status].validate(scope.presetValidationRules);
            });
        }
    }

    angular.module('meevo.components.controls')
        .directive('meevoDateRangeWithPresets', MeevoDateRangeWithPresets.factory());
}
